import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';

import { AxiosError } from 'axios';

import { API, URLs } from '@/api';
import {
    completelyExpiredDate,
    EVENT_PROCESS,
    EVENT_STATUS,
    expiredEvent,
    expiredTicket,
    TICKET,
    TICKET_ITEM,
    TICKET_PRIZE,
} from '@/models/event/fall-2610';

export const fetchEvent = async () => {
    try {
        const eventState = (await API.get(URLs.event.state)) as unknown as EVENT_STATUS;
        return eventState;
    } catch {
        return expiredEvent;
    }
};

export const fetchTicket = async () => {
    try {
        const res = (await API.get(URLs.event.tickets)) as unknown as TICKET;
        return res;
    } catch {
        return expiredTicket;
    }
};

/**
 * 1인 1회 이벤트라 eventResults는 한 건뿐이다(미보유 계정도 NO-TICKET 행 1개).
 * 조회 실패·빈 배열은 null — 호출부가 응모권 없음으로 취급한다.
 */
export const pickTicket = (res: TICKET): TICKET_ITEM | null =>
    res?.eventResults?.find(t => t.ticketStatus !== 'NO-TICKET') ?? res?.eventResults?.[0] ?? null;

export const fetchDraw = async () => {
    const { result } = (await API.post(URLs.event.draw)) as unknown as { result: TICKET_PRIZE };
    return result;
};

/**
 * 인증이 실제로 통과(CONFIRMED)한 경우에만 응모한다.
 * useEventVerification의 draw 선호출(skipDraw로 끔) 대신 쓰는 verify-first 경로 —
 * initVerify의 반환값은 NOT-ADULT·ALREADY-REGISTERED에서도 true라 서버 상태를 다시 읽어야 한다.
 * @returns 실패 시 에러 메시지, 성공·응모 불필요 시 null
 */
export const drawIfConfirmed = async (): Promise<string | null> => {
    const tk = pickTicket(await fetchTicket());
    if (tk?.verificationResult !== 'CONFIRMED') return null;

    try {
        await fetchDraw();
        return null;
    } catch (e) {
        let message: string = (e as Error).message;
        if (e instanceof AxiosError) message = e.response?.data?.message ?? message;
        // 이미 응모된 티켓은 성공으로 취급 (기존 선호출 로직과 동일)
        return message.includes('drawn') ? null : message;
    }
};

// 이벤트 종료 기한으로부터 일 수 차이 계산, 양수의 경우 now < endAt (now에서 +N일 하면 endAt)
export const daysLeft = (endAt: EVENT_STATUS['eventEndAt']) => {
    const now = DateTime.now();
    const endAtToDateTime = DateTime.fromJSDate(new Date(endAt));
    const diffDay = Math.ceil(endAtToDateTime.diff(now, 'days').days);
    return diffDay;
};

// 이벤트가 진행 상태와 상관 없이 시작을 하였는가
export const isOpened = (eventStatus: EVENT_STATUS['eventStatus']) => {
    return eventStatus !== EVENT_PROCESS.NOT_OPENED;
};

// 서버 기준 이벤트가 종료 되었는가
export const isExpired = (eventStatus: EVENT_STATUS['eventStatus']) => {
    return (
        isOpened(eventStatus) && (eventStatus === EVENT_PROCESS.EARLY_CLOSED || eventStatus === EVENT_PROCESS.EXPIRED)
    );
};

// 이벤트가 사전 종료 되었다면 그 날의 자정이 지났는가
export const isExpiredAtMidnight = (props: EVENT_STATUS) => {
    if (props.earlyClosedAt) {
        const now = DateTime.now();
        const midnight = DateTime.fromISO(props.earlyClosedAt).startOf('day').plus({ days: 1 });
        return now >= midnight;
    }

    return isExpired(props.eventStatus);
};

// endAt이 속한 주의 '다음 주 화요일 0시 0분' 반환
const getNextTuesdayMidnight = (endAt: EVENT_STATUS['eventEndAt']) => {
    const base = DateTime.fromJSDate(new Date(endAt)).startOf('day');
    const delta = (2 - base.weekday + 7) % 7 || 7;
    return base.plus({ days: delta }).startOf('day');
};

// now가 endAt 주차의 '다음 화요일 0시'를 지났는지 여부
export const isAllDisable = (endAt: EVENT_STATUS['eventEndAt'] | null) => {
    // 이벤트 완전 종료의 경우
    const now = DateTime.now();
    if (now >= completelyExpiredDate) return true;

    if (!endAt) return false;
    const nextTuesday = getNextTuesdayMidnight(endAt);
    return now >= nextTuesday;
};

export default function useEvent(immediately: boolean = false) {
    const [eventState, setEventState] = useState<EVENT_STATUS | null>(null);
    const [ticket, setTicket] = useState<TICKET_ITEM | null>(null);

    const [isECE, setIsECE] = useState<boolean>(false);
    const [mainBannerRender, setMainBannerRender] = useState<boolean>(false);
    const [expiredContainerRender, setExpiredContainerRender] = useState<boolean>(false);

    const fetchEventState = async () => {
        const res = await fetchEvent();
        return res;
    };

    const fetchTicketState = async () => {
        const res = await fetchTicket();
        return res;
    };

    const setRenderStates = (ev: EVENT_STATUS, tk: TICKET) => {
        setEventState(ev);
        setTicket(pickTicket(tk));

        // 이벤트 완전 종료 (종료 상태 + 종료된 날(사전 종료 earlyClosedAt / 정상 종료 eventEndAt) 기준 다음 화요일 경과)
        const allDisable = isAllDisable(ev.earlyClosedAt ?? ev.eventEndAt);
        setIsECE(allDisable);
        // 이벤트 진행 중
        const isEventOpened = !allDisable && isOpened(ev.eventStatus);
        // 메인 배너 = 이벤트 종료 후 자정까지
        const mainBannerCondition = isEventOpened && !isExpiredAtMidnight(ev);
        setMainBannerRender(mainBannerCondition);
        // 이벤트가 종료된 경우 (이벤트가 아직 열리지 않은 경우(NOT_OPENED) 포함)
        const expiredContainerCondition = isExpired(ev.eventStatus) || ev.eventStatus === EVENT_PROCESS.NOT_OPENED;
        setExpiredContainerRender(expiredContainerCondition);
    };

    const init = () => {
        fetchEventState().then(es => fetchTicketState().then(ts => setRenderStates(es, ts)));
    };

    useEffect(() => {
        if (immediately) init();
    }, []);

    return {
        init,
        eventState,
        ticket,
        isECE,
        mainBannerRender,
        expiredContainerRender,
        fetchEventState,
        fetchTicketState,
    };
}
